import type { State } from '@lucimark/kalman-filter'
import type { IPositionMeasurement } from '@lucimark/shared/models/geolocation'
import { KalmanFilter } from '@lucimark/kalman-filter'

export const roundToDecimals = (n: number, decimals: number) => Number(n.toFixed(decimals))

/**
 * Minimal position shape the filter consumes. Both the DOM `GeolocationPosition`
 * and the Tauri geolocation plugin `Position` satisfy this structurally, so the
 * player can feed either backend through the same pipeline.
 */
export interface IGeolocationSample {
  timestamp: number
  coords: {
    latitude: number
    longitude: number
    accuracy: number
  }
}

export interface IKalman {
  update: (position: IGeolocationSample) => IPositionMeasurement
  reset: () => void
}

export default class Kalman implements IKalman {
  private readonly coordinateDecimalPlaces: number
  private previousCorrected?: State
  private kalman: KalmanFilter

  constructor(options: { coordinateDecimalPlaces: number }) {
    const { coordinateDecimalPlaces } = options
    this.kalman = new KalmanFilter({ observation: 2 })
    this.coordinateDecimalPlaces = coordinateDecimalPlaces
  }

  update(position: IGeolocationSample): IPositionMeasurement {
    const {
      timestamp,
      coords: { latitude: lat, longitude: long, accuracy }
    } = position

    const observation = [
      roundToDecimals(lat, this.coordinateDecimalPlaces),
      roundToDecimals(long, this.coordinateDecimalPlaces)
    ]

    const predicted = this.kalman.predict({ previousCorrected: this.previousCorrected })
    this.previousCorrected = predicted

    const correctedState = this.kalman.correct({
      predicted,
      observation
    })

    const [latitude, longitude] = correctedState.mean.flat() as [number, number]

    const measurement: IPositionMeasurement = {
      timestamp,
      latitude: roundToDecimals(latitude, this.coordinateDecimalPlaces),
      longitude: roundToDecimals(longitude, this.coordinateDecimalPlaces),
      accuracy: roundToDecimals(accuracy, 0)
    }

    return measurement
  }

  reset() {
    this.previousCorrected = undefined
  }
}
